import React, { useEffect, useState, useRef } from "react";
import "./Ths.css";
import SearchInput from "../../SearchInput/SearchInput";
import SortIcons from "../../SortIcons/SortIcons";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="14"
    height="14"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const Ths = ({
  column,
  data,
  onSearch,
  handleSort,
  activeSearch,
  setActiveSearch,
  activeSort,
  setActiveSort,
  transactionTable,
  sortConfig,
  // New props for multiple search support
  isSearchActive = false,
  searchValue = "",
  columnDisplayName = "",
}) => {
  const [searchTerm, setSearchTerm] = useState(searchValue || "");
  const dispatch = useDispatch();
  const searchRef = useRef(null);
  const datepickerRef = useRef(null);

  // Ops Home table data (used when NOT in transaction table mode)
  const tableData = useSelector(
    (state) => state.operationHome.table.data || [],
  );

  // Transaction popup table data (used when transactionTable=true)
  const transactionTableData = useSelector(
    (state) => state.agentHome.transactionTable?.data || [],
  );

  const activeTab = useSelector((state) => state.operationHome.activeTab);

  const detailsData = useSelector((state) => state.operationHome.details.data);

  const workScreenAssignee = useSelector(
    (state) => state.operationHome.workScreenData?.assignee || [],
  );

  // Resolve the final options array for dropdown columns.
  // When rendering inside the TransactionPopup (transactionTable=true), use
  // transactionTableData so agent names and call categories are derived from
  // the popup's own data, not the Ops Home table.
  const resolvedDropdownOptions = (() => {
    if (column.dropdownOptions) return column.dropdownOptions;

    const sourceData = transactionTable ? transactionTableData : tableData;

    if (column.dynamicDropdown === "agentNames") {
      // For Ops Home, prefer the authoritative assignee list when available
      if (!transactionTable && workScreenAssignee.length > 0) {
        return workScreenAssignee
          .map((a) => a.assigneeName)
          .filter(Boolean)
          .sort();
      }
      return [
        ...new Set(sourceData.map((r) => r.agentName).filter(Boolean)),
      ].sort();
    }
    if (column.dynamicDropdown === "callCategories") {
      // Use allcategories from the first row when available (Ops Home enriched data)
      const allCats = sourceData[0]?.allcategories;
      if (Array.isArray(allCats) && allCats.length > 0) {
        return allCats.filter(Boolean);
      }
      return [
        ...new Set(sourceData.map((r) => r.callCategory).filter(Boolean)),
      ].sort();
    }
    return null;
  })();

  const isDropdownColumn = column.searchInput && resolvedDropdownOptions;

  // Sync search term with parent activeSearches
  useEffect(() => {
    if (searchValue !== undefined && searchValue !== null) {
      // For date columns, format from YYYY-MM-DD to MM/DD/YYYY
      if (column.id === "engagementDate" && searchValue) {
        try {
          const [year, month, day] = searchValue.split("-");
          if (year && day && month) setSearchTerm(`${month}/${day}/${year}`);
        } catch (error) {
          setSearchTerm(searchValue);
        }
      } else {
        setSearchTerm(searchValue);
      }
    } else {
      setSearchTerm("");
    }
  }, [searchValue, column.id]);

  // Focus on input when column becomes active search
  useEffect(() => {
    if (isSearchActive && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isSearchActive]);

  // Handle Enter key for immediate search
  const handleSearchEnter = (e) => {
    // console.log ("key pressed: ", e.key)
    if (e.key === "Enter") {
      performSearch(searchTerm);
      dispatch(setActiveSearch(column.id));
      dispatch(setActiveSort(""));
    }
  };

  // Perform the actual search
  const performSearch = (value) => {
    let formattedValue = value;

    // Format date for API if needed
    if (column.id === "engagementDate" && value) {
      try {
        const [month, day, year] = value.split("/");
        if (month && day && year)
          formattedValue = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        else formattedValue = value.replace("/", "-");
      } catch (error) {
        formattedValue = value;
      }
    }

    console.log(formattedValue);

    onSearch(column.id, formattedValue);

    // Update active search state
    if (value.trim() !== "") {
      dispatch(setActiveSearch(column.id));
    } else {
      dispatch(setActiveSearch(""));
    }
  };

  // Clear search for this column
  const handleClearSearch = () => {
    setSearchTerm("");
    onSearch(column.id, "");
    dispatch(setActiveSearch(""));
  };

  const handleSearchInput = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <th>
      <div className="header-content">
        <div className={`header header-${column.id}`}>
          <span className={column.sortIcons ? "" : "th_without_sort"}>
            {column.label}
          </span>
          {column.sortIcons && (
            <SortIcons
              className="sorting-button"
              columnKey={column.sortExpression || column.id}
              data={data}
              handleSort={handleSort}
              sortConfig={sortConfig}
              transactionTable={transactionTable}
            />
          )}
        </div>
        <div className="search-cont">
          {isDropdownColumn ? (
            <div className="search-input-wrapper">
              <select
                className={`search-input dropdown-filter ${
                  column.dynamicDropdown === "agentNames"
                    ? "agent-dropdown"
                    : ""
                }`}
                value={searchTerm}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchTerm(val);
                  performSearch(val);
                }}
              >
                <option value="" disabled hidden>
                  -Select-
                </option>
                {resolvedDropdownOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {column.dropdownLabels?.[opt] ?? opt}
                  </option>
                ))}
              </select>
            </div>
          ) : column.searchInput && column.id === "engagementDate" ? (
            <div className={`search-input-wrapper date-picker-wrapper${activeTab === "range" ? " date-picker-disabled" : ""}`}>
              <DatePicker
                ref={datepickerRef}
                selected={(() => {
                  if (!searchTerm) return null;
                  const [month, day, year] = searchTerm.split("/");
                  if (!month || !day || !year) return null;
                  const d = new Date(+year, +month - 1, +day);
                  return isNaN(d.getTime()) ? null : d;
                })()}
                onChange={(date) => {
                  if (date) {
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    const year = date.getFullYear();
                    const formatted = `${month}/${day}/${year}`;
                    setSearchTerm(formatted);
                    performSearch(formatted);
                  } else {
                    setSearchTerm("");
                    onSearch(column.id, "");
                    dispatch(setActiveSearch(""));
                  }
                }}
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                maxDate={new Date()}
                disabled={activeTab === "range"}
                className="date-text-input"
                wrapperClassName="rdp-wrapper"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                popperPlacement="bottom-start"
                popperProps={{ strategy: "fixed" }}
              />
              <button
                className="calendar-icon-btn"
                type="button"
                disabled={activeTab === "range"}
                onClick={() => datepickerRef.current?.setOpen(true)}
              >
                <CalendarIcon />
              </button>
            </div>
          ) : column.searchInput ? (
            <div className="search-input-wrapper">
              <SearchInput
                ref={searchRef}
                className={`search-input ${isSearchActive ? "active-search-input" : ""}`}
                placeholder={
                  column.searchPlaceholder ||
                  `Search ${columnDisplayName || column.label}`
                }
                value={searchTerm}
                onChange={handleSearchInput}
                onSearch={handleSearchEnter}
                type="text"
                style={{
                  borderColor: isSearchActive ? "#1976d2" : "#ccc",
                  backgroundColor: isSearchActive ? "#e3f2fd" : "white",
                  fontWeight: isSearchActive ? "600" : "normal",
                }}
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={handleClearSearch}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          ) : (
            <input
              type="text"
              placeholder="N/A"
              disabled
              className="non-search-input"
            />
          )}
        </div>
      </div>
    </th>
  );
};

export default Ths;
